
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Download, Trash2, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import PageTransition from '@/components/PageTransition';

interface Conversation {
  id: string;
  userMessage: {
    content: string;
    timestamp: Date;
  };
  aiMessage: {
    content: string;
    timestamp: Date;
  };
  path: string;
  sessionId: string;
  timestamp: string;
  settings: {
    provider: string;
    model: string;
  };
}

const ConversationData = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if the user is authenticated as admin
    const authData = localStorage.getItem('adminAuth');
    if (!authData || !JSON.parse(authData).authenticated) {
      toast.error('You must be logged in as admin to view this page');
      navigate('/login', { state: { isAdmin: true } });
      return;
    }

    // Load conversation data from localStorage
    const loadConversations = () => {
      try {
        const storedConversations = localStorage.getItem('aiConversations');
        if (storedConversations) {
          const parsedConversations = JSON.parse(storedConversations);
          
          // Add unique IDs if they don't exist
          const conversationsWithIds = parsedConversations.map((conv: any, index: number) => ({
            ...conv,
            id: conv.id || `conv-${index}-${Date.now()}`
          }));
          
          setConversations(conversationsWithIds);
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
        toast.error('Failed to load conversation data');
      } finally {
        setIsLoading(false);
      }
    };

    loadConversations();
  }, [navigate]);

  // Filter conversations based on search term
  const filteredConversations = conversations.filter(
    (conv) => 
      conv.userMessage.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.aiMessage.content.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Delete a conversation
  const handleDeleteConversation = (id: string) => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      const updatedConversations = conversations.filter(conv => conv.id !== id);
      setConversations(updatedConversations);
      localStorage.setItem('aiConversations', JSON.stringify(updatedConversations));
      toast.success('Conversation deleted');
    }
  };
  
  // Clear all conversations
  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to delete all conversations? This cannot be undone.')) {
      setConversations([]);
      localStorage.removeItem('aiConversations');
      toast.success('All conversations cleared');
    }
  };

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Admin - Conversation Data</h1>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                Last 30 Days
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
              <Button variant="destructive" size="sm" onClick={handleClearAll}>
                <Trash2 className="mr-2 h-4 w-4" />
                Clear All
              </Button>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          {/* Search and stats */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="col-span-1 md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardDescription>Total Conversations</CardDescription>
                <CardTitle className="text-2xl">{conversations.length}</CardTitle>
              </CardHeader>
            </Card>
          </div>
          
          {/* Conversations list */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center p-8 border rounded-lg bg-gray-50">
                <p className="text-muted-foreground">No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <Card key={conversation.id} className="overflow-hidden">
                  <CardHeader className="p-4 pb-2 bg-muted/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-sm font-normal">
                          Session ID: <span className="font-mono">{conversation.sessionId}</span>
                        </CardTitle>
                        <CardDescription>
                          {new Date(conversation.timestamp).toLocaleString()} • {conversation.path}
                        </CardDescription>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={() => handleDeleteConversation(conversation.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-xs font-medium">User</span>
                        </div>
                        <Label className="text-sm font-medium">User Message</Label>
                      </div>
                      <div className="pl-10">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {conversation.userMessage.content}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">AI</span>
                        </div>
                        <Label className="text-sm font-medium">AI Response</Label>
                      </div>
                      <div className="pl-10">
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {conversation.aiMessage.content}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 pt-0 border-t bg-muted/10">
                    <div className="flex justify-between items-center w-full text-xs text-muted-foreground">
                      <div>
                        Model: <span className="font-medium">{conversation.settings?.model || 'Not specified'}</span>
                      </div>
                      <div>
                        Provider: <span className="font-medium">{conversation.settings?.provider || 'Not specified'}</span>
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              ))
            )}
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default ConversationData;
